import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  ClinicalPageShell,
  SignalBar,
  SectionPanel,
  ClinicalDataGrid,
  tokens,
  fmtMoney,
} from "../../shared/clinical-design";
import MlrTrendChart from "../components/MlrTrendChart";
import RevenueLeversPanel from "../components/RevenueLeversPanel";
import {
  getExecutiveFinancials,
  fmtMillions,
} from "../services/executiveFinancialService";

const driverColumns = [
  { key: "label", header: "Cost driver" },
  {
    key: "annual",
    header: "Annual cost",
    align: "right",
    mono: true,
    render: (r) => fmtMoney(r.annual),
  },
  {
    key: "pctOfTotal",
    header: "% of total",
    align: "right",
    mono: true,
    render: (r) => `${r.pctOfTotal.toFixed(1)}%`,
  },
  {
    key: "pmpm",
    header: "PMPM",
    align: "right",
    mono: true,
    render: (r) => fmtMoney(r.pmpm, { maximumFractionDigits: 2 }),
  },
  {
    key: "trendPct",
    header: "Trend MoM",
    align: "right",
    render: (r) => (
      <Typography
        component="span"
        sx={{
          fontFamily: tokens.fontMono,
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
          color: r.trendPct > 0 ? tokens.crit : tokens.ok,
        }}
      >
        {r.trendPct > 0 ? "▲" : "▼"} {Math.abs(r.trendPct).toFixed(1)}%
      </Typography>
    ),
  },
  {
    key: "share",
    header: "Cost share",
    width: 180,
    render: (r) => (
      <Box sx={{ width: 160, height: 7, bgcolor: tokens.surface2, borderRadius: "2px", overflow: "hidden" }}>
        <Box
          sx={{
            width: `${Math.min(r.pctOfTotal, 100)}%`,
            height: "100%",
            bgcolor: r.trendPct > 3 ? tokens.crit : tokens.info,
            opacity: 0.75,
          }}
        />
      </Box>
    ),
  },
];

/**
 * Module K — Executive Financial Dashboard.
 * Una sola vista para la directiva: MLR vs meta, déficit, drivers del costo
 * médico y cuánto mueve la aguja cada módulo operativo (E / N / G).
 */
export default function ExecutiveFinancialPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await getExecutiveFinancials();
        if (active) setData(result);
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const s = data?.signals;
  const signals = s
    ? [
        {
          label: "MLR",
          value: `${s.mlr.toFixed(1)}%`,
          tone: s.mlr > s.mlrTarget ? "crit" : "ok",
          trend: `target ${s.mlrTarget.toFixed(0)}%`,
          hint: "Medical Loss Ratio (LAB8R) vs contractual target",
        },
        {
          label: "Net margin",
          value: `${s.netMarginPct.toFixed(1)}%`,
          tone: s.netMarginPct < 0 ? "crit" : "ok",
        },
        {
          label: "Revenue PMPM",
          value: fmtMoney(s.revenuePmpm),
          tone: "info",
          hint: "Average premium per member per month",
        },
        {
          label: "Cost PMPM",
          value: fmtMoney(s.costPmpm, { maximumFractionDigits: 0 }),
          tone: "warn",
          hint: "Claims-based medical cost per member per month",
        },
        {
          label: "Deficit YTD",
          value: fmtMillions(s.deficitYtd),
          tone: "crit",
          hint: "Year-to-date operating deficit (LAB8R)",
        },
        {
          label: "Risk score",
          value: s.riskScore.toFixed(3),
          tone: "muted",
        },
      ]
    : [];

  return (
    <ClinicalPageShell
      title="Executive Financial Dashboard"
      subtitle="Single executive view: MLR against target, deficit drivers and the revenue levers each operational module is moving."
      moduleTag="Module K"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 2 }} />

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 2 }}>
        <SectionPanel title="MLR trend vs target — last 12 months" sx={{ flex: 7, minWidth: 0 }}>
          <MlrTrendChart data={data?.mlrTrend || []} target={s?.mlrTarget ?? 86} loading={loading} />
        </SectionPanel>
        <SectionPanel title="Revenue levers (Modules E · N · G)" sx={{ flex: 5, minWidth: 0 }}>
          <RevenueLeversPanel levers={data?.revenueLevers || []} loading={loading} />
        </SectionPanel>
      </Stack>

      <SectionPanel
        title="Medical cost drivers"
        actions={
          data ? (
            <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.ink2 }}>
              Total cost of care {fmtMoney(data.totalCost)}
            </Typography>
          ) : null
        }
        contentSx={{ p: 0 }}
      >
        <ClinicalDataGrid
          columns={driverColumns}
          rows={data?.costDrivers || []}
          rowKey={(r) => r.key}
          loading={loading}
          emptyMessage="No financial data available for this period."
          maxHeight={360}
          sx={{ border: 0 }}
        />
      </SectionPanel>
      <Box sx={{ height: 8 }} />
    </ClinicalPageShell>
  );
}
